/**
 * CopyDevLogButton Component
 *
 * Button for copying console error log to clipboard.
 * Shows error count badge and provides feedback on copy success/failure.
 *
 * Features:
 * - Copy console errors to clipboard
 * - Error count badge (when errors exist)
 * - Visual feedback (toast notification)
 * - Mobile-friendly touch target (44px min)
 * - Matches SyncButton styling
 *
 * Usage:
 * ```tsx
 * <CopyDevLogButton variant="full" className="w-full" />
 * ```
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../ui';
import { Bug, CheckCircle, XCircle } from 'lucide-react';
import { getFormattedErrorLog, getErrorCount, clearErrorLog } from '@shared/utils/consoleErrorCapture';
import toast from 'react-hot-toast';

interface CopyDevLogButtonProps {
  /**
   * Button variant
   * - "compact": Icon only with error badge (for toolbars)
   * - "full": Full width with text label (for mobile sidebar)
   */
  variant?: 'compact' | 'full';
  className?: string;
}

/**
 * Button to copy development error log to clipboard
 *
 * @example
 * ```tsx
 * // Compact variant (toolbar)
 * <CopyDevLogButton variant="compact" />
 *
 * // Full variant (mobile sidebar)
 * <CopyDevLogButton variant="full" className="w-full justify-center" />
 * ```
 */
export function CopyDevLogButton({ variant = 'compact', className = '' }: CopyDevLogButtonProps) {
  const [errorCount, setErrorCount] = useState(0);
  const [isCopying, setIsCopying] = useState(false);

  // Update error count every 2 seconds
  useEffect(() => {
    const updateCount = () => {
      setErrorCount(getErrorCount());
    };

    updateCount(); // Initial count
    const interval = setInterval(updateCount, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleCopy = async () => {
    if (isCopying) return;

    setIsCopying(true);

    try {
      const log = getFormattedErrorLog();

      // Copy to clipboard
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(log);
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = log;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      // Show success toast
      toast.success(
        errorCount === 0
          ? 'No errors to copy'
          : `Copied ${errorCount} error${errorCount === 1 ? '' : 's'} to clipboard`,
        {
          icon: <CheckCircle className="w-5 h-5" />,
          duration: 3000,
        }
      );

      console.log('[CopyDevLogButton] Error log copied to clipboard');
    } catch (error) {
      console.error('[CopyDevLogButton] Failed to copy log:', error);

      // Show error toast
      toast.error('Failed to copy error log', {
        icon: <XCircle className="w-5 h-5" />,
        duration: 3000,
      });
    } finally {
      setIsCopying(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearErrorLog();
    setErrorCount(0);
    toast.success('Error log cleared', {
      duration: 2000,
    });
  };

  if (variant === 'full') {
    return (
      <div className="relative">
        <Button
          onClick={handleCopy}
          disabled={isCopying}
          variant="secondary"
          size="sm"
          className={className}
          data-testid='copy-dev-log-btn'
          data-ux='copy-dev-log'
          title={errorCount === 0 ? 'No errors captured' : `Copy ${errorCount} error${errorCount === 1 ? '' : 's'} to clipboard`}>
          <Bug className="w-4 h-4 mr-2" />
          {isCopying ? 'Copying...' : 'Copy Dev Log'}
          {errorCount > 0 && (
            <span className="ml-2 px-1.5 py-0.5 text-xs font-bold bg-red-600 text-white rounded-full min-w-[20px] text-center">
              {errorCount > 99 ? '99+' : errorCount}
            </span>
          )}
        </Button>
        {errorCount > 0 && (
          <button
            onClick={handleClear}
            className="absolute -top-1 -right-1 w-5 h-5 bg-dark-bg border border-dark-border/30 rounded-full flex items-center justify-center text-dark-muted hover:text-dark-text hover:bg-dark-surface/50 transition-all duration-150"
            title="Clear error log"
            aria-label="Clear error log">
            <XCircle className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  }

  // Compact variant (icon only with badge)
  return (
    <div className="relative">
      <Button
        onClick={handleCopy}
        disabled={isCopying}
        variant="ghost"
        size="sm"
        className={className}
        data-testid='copy-dev-log-btn'
        data-ux='copy-dev-log'
        title={errorCount === 0 ? 'No errors captured' : `Copy ${errorCount} error${errorCount === 1 ? '' : 's'} to clipboard`}>
        <Bug className="w-4 h-4" />
      </Button>
      {errorCount > 0 && (
        <span
          className="absolute -top-1 -right-1 px-1.5 py-0.5 text-xs font-bold bg-red-600 text-white rounded-full min-w-[18px] text-center"
          aria-label={`${errorCount} error${errorCount === 1 ? '' : 's'} captured`}>
          {errorCount > 99 ? '99+' : errorCount}
        </span>
      )}
    </div>
  );
}
